import { ReactNode, forwardRef } from 'react';
import { Container, ContainerProps } from '@mantine/core';
import { usePreferencesStore, selectLayoutWidth, selectLayoutPadding } from '@/stores/preferencesStore';
import { LayoutManagedContext } from './layoutContext';

interface LayoutContainerProps extends Omit<ContainerProps, 'fluid' | 'size'> {
  children: ReactNode;
  /** Override the preference (for pages that must be full-width) */
  forceFullWidth?: boolean;
}

// Horizontal padding for full-width mode. Per design, NO horizontal padding on
// mobile or tablet — content goes edge-to-edge — and padding only kicks in at
// the `lg` breakpoint and above.
const PADDING_MAP = {
  xs: { base: 0, lg: 24 },   // Extra Small
  sm: { base: 0, lg: 48 },   // Small
  md: { base: 0, lg: 72 },   // Medium (default)
  lg: { base: 0, lg: 96 },   // Large
  xl: { base: 0, lg: 120 },  // Extra Large
};

/**
 * LayoutContainer - Applies layout width preference to page content
 *
 * Automatically reads the user's layout width preference and applies appropriate Container sizing.
 *
 * - Boxed mode: Uses Container size="xl" (1320px max-width)
 * - Full-width mode: Uses Container fluid with responsive horizontal padding that scales from mobile to desktop
 * - Padding automatically reduces on smaller screens to preserve content space
 * - Use forceFullWidth prop for pages that require full-width regardless of preference (e.g., Board view)
 *
 * @example
 * ```tsx
 * // Standard usage
 * <LayoutContainer py="xl">
 *   <YourContent />
 * </LayoutContainer>
 *
 * // Force full-width (e.g., for Board view)
 * <LayoutContainer forceFullWidth>
 *   <KanbanBoard />
 * </LayoutContainer>
 * ```
 */
const LayoutContainer = forwardRef<HTMLDivElement, LayoutContainerProps>(
  ({ children, forceFullWidth = false, ...containerProps }, ref) => {
    const layoutWidth = usePreferencesStore(selectLayoutWidth);
    const layoutPadding = usePreferencesStore(selectLayoutPadding);

    // Mark everything below as a "managed layout" so themed <Container>s used by
    // individual pages defer their width/horizontal-padding to the global Layout
    // Preference applied here (instead of capping width with their own size).
    const managedChildren = (
      <LayoutManagedContext.Provider value={true}>{children}</LayoutManagedContext.Provider>
    );

    // IMPORTANT: every branch below must render the same component type so
    // toggling `forceFullWidth` or the user's width preference doesn't change
    // the children's parent in the React tree. A type change here would cause
    // the entire page (including useState) to remount, which manifested as
    // pages "losing their selection" and re-firing queries on layout flips.

    // Forced full-width: fluid container with no horizontal padding so the
    // content can truly span the viewport (used for Kanban boards).
    if (forceFullWidth) {
      return (
        <Container ref={ref} className="app-layout-root" fluid px={0} {...containerProps}>
          {managedChildren}
        </Container>
      );
    }

    // If preference is full-width, use fluid container with configurable padding
    if (layoutWidth === 'full-width') {
      return (
        <Container ref={ref} className="app-layout-root" fluid px={PADDING_MAP[layoutPadding]} {...containerProps}>
          {managedChildren}
        </Container>
      );
    }

    // Default: boxed layout with xl size (1320px max-width). No horizontal
    // padding on mobile/tablet; padding only at lg+.
    return (
      <Container ref={ref} className="app-layout-root" size="xl" px={{ base: 0, lg: 'md' }} {...containerProps}>
        {managedChildren}
      </Container>
    );
  }
);

LayoutContainer.displayName = 'LayoutContainer';

export default LayoutContainer;
